import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'

/**
 * Fluent 2 Button Component
 * https://fluent2.microsoft.design/components/web/react/core/button/usage
 *
 * Uses Fluent 2 motion tokens for transitions:
 * - Duration: 150ms (fast) for hover/active states
 * - Easing: cubic-bezier(0.33, 0, 0.67, 1) for smooth feel
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 rounded-md text-sm font-medium transition-all duration-150 ease-[cubic-bezier(0.33,0,0.67,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none active:scale-[0.98]',
  {
    variants: {
      variant: {
        // Primary - filled brand color (using neutral/black for Fluent 2 style)
        default: 'bg-[#1a1a1a] text-white hover:bg-[#333] active:bg-[#0a0a0a] focus-visible:ring-[#1a1a1a] disabled:bg-neutral-100 disabled:text-neutral-400 dark:bg-white dark:text-[#1a1a1a] dark:hover:bg-[#f5f5f5] dark:active:bg-[#e5e5e5] dark:disabled:bg-neutral-800 dark:disabled:text-neutral-500 shadow-[0_1px_2px_rgba(0,0,0,0.12),0_0_2px_rgba(0,0,0,0.08)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.14),0_0_2px_rgba(0,0,0,0.12)]',
        // Destructive/Danger
        destructive: 'bg-[#c50f1f] text-white hover:bg-[#a80000] active:bg-[#8b0000] focus-visible:ring-[#c50f1f] disabled:bg-neutral-100 disabled:text-neutral-400 dark:bg-[#dc626d] dark:hover:bg-[#e37d87] dark:active:bg-[#dc626d]',
        // Outline/Secondary - bordered
        outline: 'border border-[#e5e5e5] bg-transparent text-[#1a1a1a] hover:bg-[#f5f5f5] active:bg-[#e5e5e5] focus-visible:ring-neutral-400 disabled:border-neutral-200 disabled:text-neutral-400 dark:border-[#3d3d3d] dark:text-white dark:hover:bg-[#3d3d3d] dark:active:bg-[#525252]',
        // Secondary - filled neutral
        secondary: 'bg-[#f5f5f5] text-[#1a1a1a] hover:bg-[#e5e5e5] active:bg-[#d4d4d4] focus-visible:ring-neutral-400 disabled:bg-neutral-100 disabled:text-neutral-400 dark:bg-[#3d3d3d] dark:text-white dark:hover:bg-[#525252] dark:active:bg-[#616161]',
        // Ghost/Subtle - minimal
        ghost: 'text-[#1a1a1a] hover:bg-[#f5f5f5] active:bg-[#e5e5e5] focus-visible:ring-neutral-400 disabled:text-neutral-400 dark:text-white dark:hover:bg-[#3d3d3d] dark:active:bg-[#525252]',
        // Transparent
        transparent: 'text-[#1a1a1a] hover:bg-black/5 active:bg-black/10 focus-visible:ring-neutral-400 disabled:text-neutral-400 dark:text-white dark:hover:bg-white/5 dark:active:bg-white/10',
        // Link
        link: 'text-[#1a1a1a] underline-offset-4 hover:underline focus-visible:ring-[#1a1a1a] dark:text-white',
        // Success
        success: 'bg-[#0e7a0d] text-white hover:bg-[#0c6a0b] active:bg-[#0a5a09] focus-visible:ring-[#0e7a0d] disabled:bg-neutral-100 disabled:text-neutral-400',
      },
      size: {
        sm: 'h-7 px-3 text-xs',
        default: 'h-9 px-4',
        lg: 'h-11 px-6',
        icon: 'h-9 w-9',
        'icon-sm': 'h-7 w-7',
        'icon-lg': 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      )
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-fluent-spin" />}
        {children}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
